import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Bot, Clock } from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

export default function AgentLogsPage() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/agent-logs?limit=50');
      setLogs(res.data);
    } catch (err) {
      console.error('Agent logs error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="agent-logs-page" className="space-y-6">
      <motion.div {...fadeUp}>
        <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <Bot size={28} className="text-blue-400" />
          Agent Run Logs
        </h1>
        <p className="text-slate-400 text-sm font-manrope mt-0.5">
          Execution history of all AI agents
        </p>
      </motion.div>

      <div className="space-y-2">
        {logs.map((log, i) => (
          <motion.div
            key={log.id || i}
            {...fadeUp}
            transition={{ delay: i * 0.03, duration: 0.3 }}
            className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:border-blue-500/15 transition-all flex items-center gap-4"
            data-testid={`agent-log-${i}`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              log.agent_name === 'stake_reliability' ? 'bg-blue-500/20 text-blue-400' :
              log.agent_name === 'incentive_optimizer' ? 'bg-lime-500/20 text-lime-400' :
              log.agent_name === 'fraud_detection' ? 'bg-pink-500/20 text-pink-400' :
              'bg-orange-500/20 text-orange-400'
            }`}>
              <Bot size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium capitalize">{log.agent_name?.replace(/_/g, ' ')}</p>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                  log.status === 'completed' ? 'bg-lime-500/10 text-lime-400' : 'bg-yellow-500/10 text-yellow-400'
                }`}>{log.status}</span>
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock size={10} />
                  {new Date(log.start_ts).toLocaleString()}
                </span>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs text-slate-400 font-mono">Cost: {log.cost_units}</p>
              <p className="text-[10px] text-slate-600 font-mono mt-0.5">
                {log.decision_ids?.length || 0} decision(s)
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {logs.length === 0 && (
        <div className="text-center py-16 text-slate-500">
          <Bot size={48} className="mx-auto mb-3 opacity-30" />
          <p>No agent logs yet</p>
        </div>
      )}
    </div>
  );
}
